import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Video, PhoneOff } from "lucide-react";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import "@livekit/components-styles";
import { MeetingStage } from "../components/MeetingStage";
import { MeetingRecordingLayer } from "../components/MeetingRecordingLayer";

const Ctx = createContext(null);

/**
 * Keeps the admin's LiveKit meeting alive while navigating the SPA.
 * The <LiveKitRoom> lives HERE (above the routes); the meetings page only
 * provides a host <div> the stage is portaled into. Off-page → a small
 * floating widget keeps audio flowing and offers "back to meeting".
 */
export function MeetingSessionProvider({ children }) {
  const [session, setSession] = useState(null); // { meetingId, title, url, token, canRecord }
  const [host, setHost] = useState(null);

  const startSession = useCallback((s) => setSession(s), []);
  const endSession = useCallback(() => setSession(null), []);
  const value = useMemo(() => ({ session, startSession, endSession, setHost }), [session, startSession, endSession]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {session ? (
        <LiveKitRoom
          key={session.meetingId}
          serverUrl={session.url}
          token={session.token}
          connect
          audio
          video
          style={{ display: "contents" }}
          onDisconnected={endSession}
          onError={(e) => console.warn("LiveKit", e?.message)}
        >
          <MeetingSurface session={session} host={host} onLeave={endSession} />
        </LiveKitRoom>
      ) : null}
    </Ctx.Provider>
  );
}

function MeetingSurface({ session, host, onLeave }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onPage = pathname === "/meetings" && !!host;

  if (onPage) {
    return createPortal(
      <MeetingStage>
        <MeetingRecordingLayer canRecord={session.canRecord} title={session.title} />
      </MeetingStage>,
      host,
    );
  }
  return (
    <div
      data-testid="meeting-mini-widget"
      style={{
        position: "fixed", right: 18, bottom: 18, zIndex: 60, display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", borderRadius: 999, background: "#0d1b2a", border: "1px solid #c9a84c",
        boxShadow: "0 10px 30px rgba(0,0,0,0.35)", color: "#e8d59a", fontFamily: "Cinzel, serif", fontSize: "0.78rem", letterSpacing: "0.08em",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 4, background: "#3ddc97", boxShadow: "0 0 8px #3ddc97" }} />
      <span style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.title}</span>
      <button type="button" onClick={() => navigate("/meetings")} className="btn-primary-gold flex items-center gap-1" style={{ padding: "4px 10px", fontSize: "0.7rem" }} data-testid="meeting-mini-return">
        <Video size={12} /> {t("meetingsPage.backToMeeting", "REVINO")}
      </button>
      <button type="button" onClick={onLeave} title={t("meetingsPage.leave", "Părăsește")} style={{ background: "none", border: "none", color: "#e07070", cursor: "pointer" }} data-testid="meeting-mini-leave">
        <PhoneOff size={14} />
      </button>
      <RoomAudioRenderer />
    </div>
  );
}

export const useMeetingSession = () => useContext(Ctx);
